import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Plus, Trash2, Loader2, ChevronDown, ChevronUp, Bug, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE, SEVERITY_COLORS, STATUS_COLORS } from "./constants";
import { Field } from "./shared";

export default function SanitaireTab({ parcelId, refData, ilotId, ilots = [] }) {
  const ilotName = (id) => (ilots.find((i) => i.ilot_id === id) || {}).name;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/sanitaire?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`);
      if (res.ok) setItems(await res.json());
    } catch (err) {
      console.error('[SanitaireTab] fetch failed:', err);
    }
    setLoading(false);
  }, [parcelId, ilotId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    setDeletingId(null);
    try {
      const res = await authFetch(`${API_BASE}/carnet/sanitaire/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Incident supprimé");
        fetchData();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || `Erreur (${res.status})`);
      }
    } catch { toast.error("Erreur réseau"); }
  };

  if (showForm) {
    return <SanitaireForm parcelId={parcelId} ilotId={ilotId} refData={refData} existing={editingItem}
      onSaved={() => { setShowForm(false); setEditingItem(null); fetchData(); }}
      onCancel={() => { setShowForm(false); setEditingItem(null); }} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-black/5">
        <Button size="sm" className="w-full h-9 text-[11px] font-semibold rounded-lg text-white gap-1.5"
          style={{ background: "#E07A5F" }}
          onClick={() => { setEditingItem(null); setShowForm(true); }} data-testid="san-new-btn">
          <Plus className="w-3.5 h-3.5" /> Signaler un probleme
        </Button>
      </div>
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#E07A5F" }} /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Bug className="w-8 h-8 mx-auto mb-3 opacity-20" />
            <p className="text-xs" style={{ color: "#7A7A7A" }}>Aucun incident sanitaire signale</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {items.map(item => {
              const expanded = expandedId === item.incident_id;
              const sev = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.moyen;
              const stat = STATUS_COLORS[item.status] || STATUS_COLORS.actif;
              return (
                <div key={item.incident_id} className="rounded-xl border border-black/5 overflow-hidden" data-testid={`san-${item.incident_id}`}>
                  <button onClick={() => setExpandedId(expanded ? null : item.incident_id)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/[0.02]">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: sev.bg }}>
                      <Bug className="w-4 h-4" style={{ color: sev.text }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: "#1A1A1A" }}>{item.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: sev.bg, color: sev.text }}>{sev.label}</Badge>
                        <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: stat.bg, color: stat.text }}>{stat.label}</Badge>
                        <span className="text-[10px]" style={{ color: "#AAAAAA" }}>{item.date_detection}</span>
                        {item.ilot_id && ilotName(item.ilot_id) && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#dbe8f1", color: "#2f6a92" }}>{ilotName(item.ilot_id)}</Badge>}
                      </div>
                    </div>
                    {expanded ? <ChevronUp className="w-3.5 h-3.5 opacity-40" /> : <ChevronDown className="w-3.5 h-3.5 opacity-40" />}
                  </button>
                  {expanded && (
                    <div className="px-3 pb-3 border-t border-black/5 pt-2 space-y-1.5">
                      {[["Categorie", item.category], ["Zone affectee", item.affected_area_percent ? `${item.affected_area_percent}%` : null],
                        ["Symptomes", item.symptoms], ["Actions", item.actions_taken], ["Notes", item.notes],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex text-[11px]">
                          <span className="w-24 shrink-0 font-medium" style={{ color: "#7A7A7A" }}>{k}</span>
                          <span style={{ color: "#1A1A1A" }}>{v}</span>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-2 pt-2 border-t border-black/5">
                        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg"
                          onClick={() => { setEditingItem(item); setShowForm(true); }}>
                          <Eye className="w-3 h-3" /> Modifier
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg text-red-500"
                          onClick={() => setDeletingId(item.incident_id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        {deletingId === item.incident_id && (
                          <div className="flex items-center gap-1.5 ml-1">
                            <span className="text-[10px] font-medium" style={{ color: "#DC2626" }}>Confirmer ?</span>
                            <Button size="sm" variant="outline" className="h-6 text-[9px] px-2 rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(item.incident_id)} data-testid="confirm-delete-btn">Oui</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1" onClick={() => setDeletingId(null)}>Non</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function SanitaireForm({ parcelId, ilotId, refData, existing, onSaved, onCancel }) {
  const [category, setCategory] = useState(existing?.category || "ravageur");
  const [name, setName] = useState(existing?.name || "");
  const [customName, setCustomName] = useState("");
  const [severity, setSeverity] = useState(existing?.severity || "moyen");
  const [affectedArea, setAffectedArea] = useState(existing?.affected_area_percent?.toString() || "");
  const [symptoms, setSymptoms] = useState(existing?.symptoms || "");
  const [actionsTaken, setActionsTaken] = useState(existing?.actions_taken || "");
  const [status, setStatus] = useState(existing?.status || "actif");
  const [dateDetection, setDateDetection] = useState(existing?.date_detection || new Date().toISOString().slice(0, 10));
  const [resolutionDate, setResolutionDate] = useState(existing?.resolution_date || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [saving, setSaving] = useState(false);

  const problems = refData?.problemes_sanitaires?.[category] || [];
  // Check if existing name is in the list, if not it's a custom one
  const isCustomExisting = existing && !problems.includes(existing.name);

  useEffect(() => {
    if (isCustomExisting && existing) {
      setName("Autre");
      setCustomName(existing.name);
    }
  }, []);

  const CATEGORIES = [
    { value: "ravageur", label: "Ravageur" },
    { value: "maladie", label: "Maladie" },
    { value: "champignon", label: "Champignon" },
    { value: "bacterie", label: "Bacterie" },
    { value: "virus", label: "Virus" },
    { value: "autre", label: "Autre" },
  ];

  const handleSubmit = async () => {
    if (!name) { toast.error("Selectionnez un probleme"); return; }
    if (name === "Autre" && !customName.trim()) { toast.error("Precisez le probleme"); return; }
    setSaving(true);
    try {
      const url = existing?.incident_id
        ? `${API_BASE}/carnet/sanitaire/${existing.incident_id}`
        : `${API_BASE}/carnet/sanitaire`;
      const method = existing?.incident_id ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          parcel_id: parcelId, ilot_id: existing ? (existing.ilot_id ?? null) : (ilotId || null), date_detection: dateDetection, category, name,
          custom_name: name === "Autre" ? customName : null,
          severity, affected_area_percent: affectedArea ? parseFloat(affectedArea) : null,
          symptoms, actions_taken: actionsTaken, status, resolution_date: resolutionDate || null, notes,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success(existing ? "Incident modifie" : "Incident signale");
      onSaved();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setSaving(false);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-3" data-testid="san-form">
        <div className="flex items-center gap-2 pb-2 border-b border-black/5">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}><ArrowLeft className="w-4 h-4" /></Button>
          <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{existing ? "Modifier" : "Signaler"} un probleme sanitaire</h3>
        </div>

        <Field label="Date de detection" value={dateDetection} onChange={setDateDetection} type="date" required testId="san-date" />

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Categorie *</Label>
          <div className="grid grid-cols-3 gap-1.5 mt-1">
            {CATEGORIES.map(c => (
              <Button key={c.value} variant={category === c.value ? "default" : "outline"} size="sm"
                className={`h-8 text-[10px] rounded-lg ${category === c.value ? "text-white" : ""}`}
                style={category === c.value ? { background: "#E07A5F" } : {}}
                onClick={() => { setCategory(c.value); setName(""); setCustomName(""); }}
                data-testid={`san-cat-${c.value}`}>
                {c.label}
              </Button>
            ))}
          </div>
        </div>

        {category !== "autre" && (
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Probleme identifie *</Label>
            <Select value={name} onValueChange={setName}>
              <SelectTrigger className="h-9 text-sm mt-1" data-testid="san-problem-select"><SelectValue placeholder="Selectionnez..." /></SelectTrigger>
              <SelectContent className="z-[9999] max-h-[250px]">
                {problems.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                <SelectItem value="Autre">Autre (personnalise)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {(name === "Autre" || category === "autre") && (
          <Field label="Description du probleme" value={customName} onChange={setCustomName}
            placeholder="Decrivez le probleme..." required testId="san-custom-name" />
        )}

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Severite</Label>
          <div className="flex gap-2 mt-1">
            {[["faible", "Faible"], ["moyen", "Moyen"], ["severe", "Severe"]].map(([val, label]) => {
              const colors = SEVERITY_COLORS[val];
              return (
                <Button key={val} variant="outline" size="sm"
                  className={`flex-1 h-9 text-xs rounded-lg border`}
                  style={severity === val ? { background: colors.bg, color: colors.text, borderColor: colors.text + "40" } : {}}
                  onClick={() => setSeverity(val)} data-testid={`san-sev-${val}`}>
                  {label}
                </Button>
              );
            })}
          </div>
        </div>

        <Field label="Zone affectee (%)" value={affectedArea} onChange={setAffectedArea}
          type="number" placeholder="Ex: 25" testId="san-area" />

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Symptomes observes</Label>
          <Textarea value={symptoms} onChange={e => setSymptoms(e.target.value)}
            placeholder="Decrivez les symptomes..." className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="san-symptoms" />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Actions entreprises</Label>
          <Textarea value={actionsTaken} onChange={e => setActionsTaken(e.target.value)}
            placeholder="Traitements, mesures prises..." className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="san-actions" />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Statut</Label>
          <div className="flex gap-2 mt-1">
            {[["actif", "Actif"], ["en_surveillance", "Surveillance"], ["resolu", "Resolu"]].map(([val, label]) => {
              const colors = STATUS_COLORS[val];
              return (
                <Button key={val} variant="outline" size="sm"
                  className={`flex-1 h-9 text-[11px] rounded-lg`}
                  style={status === val ? { background: colors.bg, color: colors.text, borderColor: colors.text + "40" } : {}}
                  onClick={() => setStatus(val)} data-testid={`san-stat-${val}`}>
                  {label}
                </Button>
              );
            })}
          </div>
        </div>

        {status === "resolu" && (
          <Field label="Date de resolution" value={resolutionDate} onChange={setResolutionDate} type="date" testId="san-resolution-date" />
        )}

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Notes</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..."
            className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="san-notes" />
        </div>

        <Button onClick={handleSubmit} disabled={saving}
          className="w-full h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#E07A5F" }}
          data-testid="san-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {existing ? "Enregistrer" : "Signaler l'incident"}
        </Button>
      </div>
    </ScrollArea>
  );
}


